import { useLayoutEffect, useEffect, useState } from "react";
import "../../assets/css/Driver.css";
import { FaTruck, FaChevronRight, FaSortAmountUpAlt, FaInfoCircle, FaTrashAlt, FaPlus, FaSearch, FaEuroSign, FaTimesCircle, FaUser, FaCheck, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { employeesAPI, bookingsAPI, usersAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { Spinner, Alert, Button, Modal, Badge, Form, InputGroup, Col, Row } from "react-bootstrap";
import Swal from 'sweetalert2';
import { formatNumber } from '../../utils/helpers';

const EmployeeBooking = () => {
  const { t } = useTranslation();
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [counts, setCounts] = useState({
    all: 0,
    regular: 0,
    fast: 0,
    completed: 0
  });
  
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [highlightedBookingId, setHighlightedBookingId] = useState(null);

  // Fetch bookings based on tab and search
  const fetchBookings = async (page = 1, search = searchTerm, tab = activeTab) => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        per_page: pagination.per_page,
        search: searchTerm,
      }

      let response;

      switch (tab) {
        case "regular":
          response = await employeesAPI.regularBookings(employeeId, params);
          break;
        case "fast":
          response = await employeesAPI.fastBookings(employeeId, params);
          break;
        case "completed":
          response = await employeesAPI.completedBookings(employeeId, params);
          break;
        default:
          response = await employeesAPI.bookings(employeeId, params);
      }

      if (response.success) {
        let fetchedData = [];
        let metaData = {};
        
        fetchedData = response.data.data || response.data;
        metaData = response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        };
        
        // ✅ تحديث counts من الـ response
        if (response.data.counts) {
          setCounts(prev => ({
            ...prev,
            all: response.data.counts.all || 0,
            regular: response.data.counts.regular || 0,
            fast: response.data.counts.fast || 0,
            completed: response.data.counts.completed || 0,
          }));
        } else {
          // ✅ محاولة استخراج counts من مصادر أخرى
          const total = response.data.total || response.total || 0;
          setCounts(prev => ({
            ...prev,
            all: total
          }));
        }

        setData(fetchedData);
        setPagination(metaData);

        navigate(location.pathname + location.search, { 
          state: { activeTab: tab, currentPage: page },
          replace: true,
        });
      } else {
        setError(response.message || t('failed_to_fetch_bookings'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_bookings'));
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchBookings();
      fetchAllCounts();
      fetchEmployee();
    } else {
      console.warn("⚠️ employeeId is missing from URL");
      setError(t("employee_id_missing") || "Employee ID is missing");
      setLoading(false);
    }
  }, [employeeId]);

  // Fetch employee details by id
  const fetchEmployee = async () => {
    try {
      const employeeResponse = await usersAPI.show(employeeId);
      if (employeeResponse.success) {
        setEmployee(employeeResponse.data.data || employeeResponse.data);
      }
    } catch (err) {
      console.error('Error fetching employee details:', err);
    }
  };

  // Fetch counts for all tabs
  const fetchAllCounts = async () => {
    try {
      // جلب عدد bookings
      const bookingResponse = await employeesAPI.bookings(employeeId, ({ per_page: 1 }));
      if (bookingResponse.success) {
        // محاولة الحصول على counts من مصادر مختلفة
        const countsData = bookingResponse.data?.counts || bookingResponse.counts || {};
        
        setCounts(prev => ({
          ...prev,
          all: countsData.all || bookingResponse.data?.total || bookingResponse.total || 0,
          regular: countsData.regular || 0,
          fast: countsData.fast || 0,
          completed: countsData.completed || 0,
        }));
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const handleMarkPaid = async (id) => {
    Swal.fire({
      title: t("confirm_action"),
      text: t("confirm_mark_paid"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("yes_mark_paid"),
      cancelButtonText: t("cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setUpdating(true);
          const response = await bookingsAPI.markPaid(id);
          
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: t("success"),
              text: t("booking.marked_paid_successfully"),
              timer: 3000,
              showConfirmButton: false,
            });
            fetchBookings();
            fetchAllCounts();
          } else {
            throw new Error(response.message);
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: err.message || t("failed_to_mark_paid"),
            timer: 3000,
          });
        } finally {
          setUpdating(false);
        }
      }
    });
  };

  // Debounced Search Effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchBookings(1, searchTerm, activeTab);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  useEffect(() => {
    // استعادة الـ state من الـ location إذا كان موجوداً
    const initialTab = location.state?.activeTab || "active";
    const initialPage = location.state?.currentPage || 1;
    const highlightId = location.state?.highlightId;
    
    setActiveTab(initialTab);
    setHighlightedBookingId(highlightId);
    
    fetchAllCounts();
    
    // تنظيف الـ state بعد الاستخدام
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, []);

  // استخدام useEffect واحد فقط لمراقبة activeTab
  useEffect(() => {
    if (activeTab && employeeId) {
      fetchBookings(1, searchTerm, activeTab);
    }
  }, [activeTab, employeeId]);

  // إضافة useEffect للتمرير إلى الـ booking المميز
  useEffect(() => {
    if (highlightedBookingId && location.state?.scrollToHighlight) {
      const element = document.getElementById(`booking-${highlightedBookingId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 1500);
      }
    }
  }, [data, highlightedBookingId]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookings(1, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.delete(deleteId);
      
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking_deleted_successfully'),
          timer: 3000,
          showConfirmButton: false
        });
        fetchBookings(pagination.current_page);
        fetchAllCounts();
      } else {
        setError(response.message || t('failed_to_delete_booking'));
      }
    } catch (err) {
      setError(t('failed_to_delete_booking'));
      console.error('Error deleting booking:', err);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleViewDetails = (id) => {
    const path = `/admin/bookings/details/${id}`;
    
    navigate(path, {
      state: {
        fromPage: pagination.current_page,
        activeTab: activeTab,
        highlightId: id
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getItemStatus = (item) => {
    if (!item) return null;
    
    // للـ Bookings مع booking
    if (item.booking?.status) return item.booking.status;
    
    return null;
  };

  const getStatusOrder = (item) => {
    const isOrder = item.order_id || item.driver_id;
    
    if (isOrder) {
      return ['pending', 'assigned', 'received', 'rejected', 'completed'];
    }
    
    return ['pending', 'approved', 'assigned', 'received', 'completed'];
  };

  const getProgressPercentage = (item) => {
    if (!item) return 0;
    
    const status = getItemStatus(item);
    if (!status) return 0;
    
    const statusOrder = getStatusOrder(item);
    const currentStatusIndex = statusOrder.indexOf(status);
    
    if (currentStatusIndex === -1) return 0;
    
    const progress = Math.round((currentStatusIndex / (statusOrder.length - 1)) * 100);
    return Math.max(0, Math.min(100, progress));
  };

  const getProgressColor = (percentage) => {
    if (percentage < 40) return "#e74c3c";
    if (percentage < 80) return "#f39c12";
    return "#2ecc71";
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      cancelled: { variant: 'danger', text: t('cancelled') },
      rejected: { variant: 'danger', text: t('rejected') },
      approved: { variant: 'success', text: t('approved') },
      assigned: { variant: 'info', text: t('assigned') },
      received: { variant: 'secondary', text: t('received') },
      completed: { variant: 'success', text: t('completed') },
      pending: { variant: 'warning', text: t('pending') }
    };
    
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (loading && data.length === 0) {
    return (
      <div className="driver-page">
        <div className="text-center py-4">
          <Spinner animation="border" />
          <p className="mt-2">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
    fetchBookings(1, '');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchBookings(1, searchTerm);
    }
  };

  // دالة لعرض العنصر حسب نوع الـ tab
  const renderItem = (item, index) => {
    const itemId = item.booking_id;
    const status = item.status;
    const user = item.user?.user_name;
    const bookingFrom = item.booking_from?.[0];
    const bookingTo = item.booking_to?.[0];
    const distance = item.distance;
    const totalWeight = item.total_weight;
    const createdAt = item.created_on;

    return (
      <div 
        className={`order-card ${item.id === highlightedBookingId ? "booking-highlight" : ""}`} 
        key={item.id}
        id={`booking-${item.id}`}
      >
        <div className="order-header">
          <span className="order-id">
            <strong>
              {searchTerm ? (
                <span 
                  dangerouslySetInnerHTML={{
                    __html: itemId?.replace(
                      new RegExp(searchTerm, 'gi'), 
                      match => `<mark class="bg-warning">${match}</mark>`
                    )
                  }} 
                />
              ) : (
                itemId
              )}
            </strong>
          </span>

          <span className="driver">
            <strong>{t("user")}:</strong> 
            {user || t('not_assigned')}
          </span>

            <span className="vehicle">
              <FaTruck />
              &nbsp;
              <small>{item.vehicle_name}</small>
            </span>
            <span className="created">{formatDate(createdAt)}</span>

          <span className="price">
            {getStatusBadge(status)}

              <Badge bg={item.payment_status ? 'success' : 'warning'} className="ms-2">
                {t(item.payment) || t('unpaid')}
              </Badge>
            {item.fast_order == 1 && (
              <Badge bg="success" className="ms-2">
                {t('booking.fast_booking')}
              </Badge>
            )}
            
            {item.invoice_status === 'canceled' && (
              <Badge bg="danger" className="ms-2">
                {t('canceled')}
              </Badge>
            )}
          </span>
        </div>

        <div className="order-body">
          <div className="left d-flex flex-wrap-wrap justify-content-between flex-wrap">
            <div className="d-flex flex-column">
              <strong>{t("booking.loading_company")}:</strong> 
              {bookingFrom || t('not_available')}
            </div>
            <div className="d-flex flex-column">
              <strong>{t("booking.unloading_company")}:</strong> 
              {bookingTo || t('not_available')}
            </div>

            <div className="delivery-path-vertical">
              <div className="circle-label-vertical">
                <span className="circle"></span>
                <span className="label">{bookingFrom}</span>
              </div>
              <div className="line-container">
                <div className="line-vertical"></div>
              </div>
              <div className="circle-label-vertical">
                <span className="circle"></span>
                <span className="label">{bookingTo}</span>
              </div>
            </div>
          </div>

          <div className="right d-flex flex-wrap-wrap justify-content-between">
            <div className="progress-bar-container mt-2">
              <div className="progress-header d-flex justify-content-between align-items-center mb-1">
                <strong>{t("progress")}:</strong>
                <span className="progress-label">{getProgressPercentage(item)}%</span>
              </div>
              
              <div className="progress-bar-outer">
                <div
                  className="progress-bar-inner"
                  style={{
                    width: `${getProgressPercentage(item)}%`,
                    background: getProgressColor(getProgressPercentage(item)),
                  }}
                ></div>
              </div>
            </div>
            
            <span className="price">
              {item.total} EUR
            </span>
          </div>
        </div>

        <div className="order-footer">
          <div>
            <strong>{t("booking.loading_date_time")}:</strong>{" "}
            {item.loading_date_time || item.created_at}
          </div>
          <div>
            <strong>{t("distance")}:</strong> {distance} | 
            <strong> {t("duration")}:</strong> {item.total_duration || item.booking?.total_duration}
          </div>
          <div>
            <strong>{t("booking.total_weight")}:</strong> {formatNumber(totalWeight)} Kg
          </div>

          <div className="action-buttons">
            {item.payment_status === 0 && (
              <Button 
                size="sm"
                variant="outline-success"
                onClick={() => handleMarkPaid(item.id)}
                disabled={updating || item.payment_status}
              >
                <FaEuroSign /> {t("booking.mark_paid")}
              </Button>
            )}
            
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => handleViewDetails(item.id)}
              className="mx-2"
            >
              <FaInfoCircle /> {t("details")}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDeleteClick(item.id)}
            >
              <FaTrashAlt /> {t("delete")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="driver-page">
        <div className="top-bar">
          {/* <div className="d-flex gap-2"> */}
            {/* Search Bar */}
            <Row className="mb-3 justify-content-between align-items-center">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={t('Search by booking ID...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  {searchTerm && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={clearSearch}
                      title={t("reset_search")}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
              <Col md={4}>
                {employee && (
                  <div>
                    <strong>{t("booking_user")}:</strong> <span className="ms-2 text-success cursor-pointer" onClick={() => navigate(`/admin/employees/details/${employee.id}`)}>{employee.user_name || employee.name || t('not_available')}</span>
                  </div>
                )}
              </Col>
            </Row>
          {/* </div> */}
          
          <div className="tabs">
            <button
              className={activeTab === "active" ? "active" : ""}
              onClick={() => handleTabChange("active")}
              disabled={loading}
            >
              <span className="count-badge">{counts.all}</span>
              {t("all_bookings")}
            </button>
            <button
              className={activeTab === "regular" ? "active" : ""}
              onClick={() => handleTabChange("regular")}
              disabled={loading}
            >
              <span className="count-badge">{counts.regular}</span>
              {t("regular_bookings")}
            </button>
            <button
              className={activeTab === "fast" ? "active" : ""}
              onClick={() => handleTabChange("fast")}
              disabled={loading}
            >
              <span className="count-badge">{counts.fast}</span>
              {t("fast_bookings")}
            </button>
            <button
              className={activeTab === "completed" ? "active" : ""}
              onClick={() => handleTabChange("completed")}
              disabled={loading}
            >
              <span className="count-badge">{counts.completed}</span>
              {t("completed_bookings")}
            </button>
          </div>
          
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="orders">
          {data.length === 0 ? (
            <div className="text-center py-5">
              <h4>{t('no_bookings_found')}</h4>
            </div>
          ) : (
            data.map((item, index) => renderItem(item, index))
          )}
        </div>

        {pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={fetchBookings}
            disabled={loading}
          />
        )}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deletion')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('confirm_delete_booking')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteCancel}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={loading}>
              {loading ? t('deleting') : t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default EmployeeBooking;